import { createAjv, JsonSchema } from '@jsonforms/core';
import { LogHelper } from '../../logging/log-helper';
import { isPlainObject } from '../../model/utility';
import { DashboardWidgetDefinition } from './dashboard-widget-definition';

export type DashboardSettingsMode = 'component' | 'schema' | 'none';

export interface DashboardSettingsResult {
  /** undefined when the definition declares no settings */
  settings: unknown;
  /** The settings failed the definition's schema, so `settings` holds the defaults instead */
  invalid?: true;
  /** Why the settings were replaced, or that the schema itself does not compile and they went unchecked */
  warning?: string;
}

const ajv = createAjv();
type CompiledSchema = { validate: ReturnType<typeof ajv.compile> } | { error: string };
const compiledSchemas = new WeakMap<JsonSchema, CompiledSchema>();

/** The settings a widget runs with: plain objects are merged over the defaults, any other instance value replaces them. */
export function effectiveSettings<S>(
  definition: DashboardWidgetDefinition<S>,
  instanceSettings: S | undefined,
): S | undefined {
  if (instanceSettings === undefined) {
    return definition.defaultSettings;
  }

  return isPlainObject(definition.defaultSettings) && isPlainObject(instanceSettings)
    ? { ...definition.defaultSettings, ...instanceSettings }
    : instanceSettings;
}

/**
 * How a widget's settings are edited: its own component (`loadSettingsComponent`, which replaces the generated
 * form; `settingsSchema` still validates), a form generated from `settingsSchema`, or not at all.
 */
export function settingsModeOf(definition: DashboardWidgetDefinition): DashboardSettingsMode {
  if (definition.loadSettingsComponent) {
    return 'component';
  }

  return definition.settingsSchema ? 'schema' : 'none';
}

export function hasSettings(definition: DashboardWidgetDefinition): boolean {
  return settingsModeOf(definition) !== 'none';
}

/**
 * Validates stored instance settings against the definition's schema; invalid settings fall back to the defaults.
 * A schema that does not compile cannot judge them, so they are kept.
 */
export function sanitizeSettings(definition: DashboardWidgetDefinition, raw: unknown): DashboardSettingsResult {
  if (definition.defaultSettings === undefined && settingsModeOf(definition) === 'none') {
    return { settings: undefined };
  }

  if (!definition.settingsSchema || raw === undefined) {
    return { settings: raw };
  }

  const compiled = compile(definition.settingsSchema, definition.type);
  if ('error' in compiled) {
    return {
      settings: raw,
      warning: `Settings schema of widget type "${definition.type}" does not compile; settings kept unchecked: ${compiled.error}`,
    };
  }

  const { validate } = compiled;
  if (validate(effectiveSettings(definition, raw))) {
    return { settings: raw };
  }

  return {
    settings: structuredClone(definition.defaultSettings),
    invalid: true,
    warning: `Settings of widget type "${definition.type}" are invalid (${ajv.errorsText(validate.errors)}); using defaults`,
  };
}

function compile(schema: JsonSchema, widgetType: string): CompiledSchema {
  let compiled = compiledSchemas.get(schema);
  if (!compiled) {
    try {
      compiled = { validate: ajv.compile(schema) };
    } catch (error) {
      LogHelper.error(
        'DashboardWidgetSettings',
        `Settings schema of widget type "${widgetType}" does not compile`,
        error,
      );
      compiled = { error: String(error) };
    }

    compiledSchemas.set(schema, compiled);
  }

  return compiled;
}
